import { useEffect } from "react";

/** Compact action bar shown at the top of a page rendered inside a tab wrapper.
 *  Replaces the top-right button that lived on the full-page PageHeader while
 *  keeping the underlying form/modal/endpoint untouched. */
export function EmbeddedActionBar({ action }) {
  if (!action) return null;
  return (
    <div className="flex justify-end mb-4" data-testid="embedded-action-bar">
      {action}
    </div>
  );
}

/** Shared editorial UI primitives — page header, empty state, chip, modal. */
export function PageHeader({ eyebrow, title, description, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 pb-8 border-b border-[#E5E5E5]">
      <div>
        {eyebrow && <div className="label-caps mb-4">{eyebrow}</div>}
        <h1 className="h-editorial text-4xl md:text-5xl">{title}</h1>
        {description && <p className="mt-4 text-[#525252] max-w-xl text-sm leading-relaxed">{description}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function Empty({ title = "Nothing here yet", hint = "Create the first entry to get started.", cta, ctaTestId }) {
  return (
    <div className="border border-dashed border-[color:var(--border,#D4D4D4)] bg-[color:var(--surface,#FAFAFA)]/50 p-16 text-center">
      <div className="h-editorial text-2xl mb-3">{title}</div>
      <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted,#737373)]">{hint}</div>
      {cta && (
        <div className="mt-6">
          <button
            type="button"
            data-testid={ctaTestId}
            onClick={cta.onClick}
            className="btn-gold inline-flex items-center gap-2"
          >
            {cta.label}
          </button>
        </div>
      )}
    </div>
  );
}

export function Chip({ children, tone = "default" }) {
  const cls = tone === "gold" ? "chip chip-gold"
    : tone === "green" ? "chip chip-green"
    : tone === "red" ? "chip chip-red"
    : tone === "blue" ? "chip chip-blue" : "chip";
  return <span className={cls}>{children}</span>;
}

export function Modal({ open, onClose, title, children, wide = false }) {
  useEffect(() => {
    if (!open || !onClose) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-10 bg-black/40 backdrop-blur-sm overflow-auto"
      onClick={onClose}
    >
      <div
        className={`card-dark w-full ${wide ? "max-w-4xl" : "max-w-xl"} p-10 atl-fade-up`}
        onClick={(e) => e.stopPropagation()}
        data-testid="modal"
      >
        <div className="flex items-start justify-between mb-8">
          <h2 className="h-editorial text-2xl">{title}</h2>
          <button
            onClick={onClose}
            className="text-[#525252] hover:text-[#0A0A0A] text-lg leading-none w-8 h-8 flex items-center justify-center border border-transparent hover:border-[#E5E5E5] transition-colors"
            data-testid="modal-close"
            aria-label="Close"
          >×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
